'use client';

import { useEffect, useState } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const STORAGE_KEY = 'repetix:install-prompt-dismissed';
const DISMISS_DAYS = 14;

export function InstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Don't show if user dismissed it recently
    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) ?? 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
      return;
    }

    // Don't show if already in standalone (installed) mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error - iOS Safari only
      window.navigator.standalone === true;
    if (isStandalone) return;

    // iOS Safari path: no beforeinstallprompt event; show manual instructions
    const ua = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOSDevice) {
      setIsIOS(true);
      setTimeout(() => setShow(true), 12000);
      return;
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
      // Delay 8s so it doesn't slap the user on first paint
      setTimeout(() => setShow(true), 8000);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!evt) return;
    await evt.prompt();
    const choice = await evt.userChoice;
    if (choice.outcome === 'accepted') {
      setShow(false);
    } else {
      dismiss();
    }
  };

  if (!show) return null;
  if (!isIOS && !evt) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 rounded-2xl border border-white/10 bg-[#18191C] shadow-2xl p-4 flex items-start gap-3"
      role="dialog"
      aria-label="Install Repetix"
    >
      <div
        className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}
        aria-hidden="true"
      >
        <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
          <path d="M16 7C11.0294 7 7 11.0294 7 16C7 20.9706 11.0294 25 16 25C20.9706 25 25 20.9706 25 16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M25 12V16H21" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 16L14.5 18.5L20 13" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm">Add Repetix to your home screen</p>
        {isIOS ? (
          <>
            <p className="text-white/60 text-xs mt-1">
              Tap{' '}
              <span aria-label="Share button" className="inline-flex items-center align-middle">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline">
                  <path d="M12 16V3" />
                  <path d="M7 8L12 3L17 8" />
                  <path d="M5 12V19A2 2 0 0 0 7 21H17A2 2 0 0 0 19 19V12" />
                </svg>
              </span>{' '}
              in Safari, then{' '}
              <span className="text-white/80 font-medium">Add to Home Screen</span>.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={dismiss}
                className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white text-xs font-medium transition-colors"
              >
                Got it
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-white/60 text-xs mt-1">Open your subs in one tap. No app store. Notifications when something renews.</p>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={install}
                className="px-3 py-1.5 rounded-lg bg-[#A855F7] hover:bg-[#C084FC] text-white text-xs font-semibold transition-colors"
              >
                Install
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white text-xs font-medium transition-colors"
              >
                Not now
              </button>
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 text-white/40 hover:text-white/80 transition-colors"
        aria-label="Dismiss"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 6L18 18M6 18L18 6" />
        </svg>
      </button>
    </div>
  );
}
